import { engineComponent } from "neo-common";
import { BaseController } from "./BaseController";
import { Paylines } from "../Paylines";
import { PaylinesSelector } from "../PaylinesSelector";
import { Symbols } from "../Symbols";
import { WinAnimator } from "../WinAnimator";
import { Machine } from "../Machine";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { GameManager } from "../../Managers/GameManager";
import { FrameTimerEventEmitter, CreateFrameTimer, FrameTimerEvents } from "../../Utils/FrameTimer";
import { PlayList } from "../../Components/Sounds/PlayList";
import { DataManager } from "../../Managers/DataManager";
import { WinAnimatorExtended } from "../../CustomComponents/WinAnimatorExtended";
import { MachineExtended } from "../../CustomComponents/MachineExtended";
import { Tweens } from "../../Components/Effects/Tweens";
import { TweenLite, TimelineLite } from "gsap";
import { GsapEffects } from "../../Components/Effects/GsapEffects";
import { MinigameElement } from "../../CustomComponents/MinigameElement";
import { TeasingBonus } from "../../CustomComponents/TeasingBonus";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { TeasingComponent } from "../../CustomComponents/TeasingComponent";
import { ShapeElement } from "../../Components/Elements/Graphics/ShapeElement";
import { ModuleManager } from "../../Managers/ModuleManager";

@engineComponent(ComponentTypes.DisplayObject)
export class BaseView extends ContainerElement {
    protected _controller: BaseController;

    private _wasFinished: boolean = false;

    private _outroDelay: FrameTimerEventEmitter;
    private _gambleMobileDelay: FrameTimerEventEmitter;

    private _paylines: Paylines;
    private _paylinesSelector: PaylinesSelector;
    private _symbols: Symbols;
    private _machine: MachineExtended;
    protected _winAnimator: WinAnimatorExtended;

    protected _minigame: MinigameElement;

    private transitionAlphaTween: Array<number>;
    public transitionAlphaTweenEase: string;
    public transitionAlphaTweenDelay: number;
    public transitionAlphaTweenDuration: number;
    private transitionAlphaTweenKeyTimes: Array<number>;

    private _teasingBonus: TeasingBonus;
    protected _transition: SwitchElement;

    private _wildContainer: ContainerElement;

    private _background: SwitchElement;
    private _foreground: SwitchElement;

    protected _gamble: HTMLElement;
    protected _controllerTween: any;

    constructor() {
        super();

        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));

        this._gambleMobileDelay = CreateFrameTimer("gambleMobileDelay");
        this._gambleMobileDelay.on(FrameTimerEvents.Complete, this.gambleMobileDelay.bind(this));

        this._gamble = document.getElementById("gamble");

        this._teasingBonus = new TeasingBonus();
        this._teasingBonus.onLuciferWildAnimation((animation: number) => {
            if (this._foreground != null) {
                if (animation === 0) {
                    // this._background.goToCase("WIN_WILD");
                    this._foreground.goToCase("WIN_WILD");
                } else if (animation === 1) {
                    // this._background.goToCase("THROW_WILD");
                    this._foreground.goToCase("THROW_WILD");
                } else if (animation === 2) {
                    // this._background.goToCase("THROW_WILD");
                    this._foreground.goToCase("TEASING_OFF");
                }
            }
        });
    }

    createByNode(node: Element): void {
        super.createByNode(node);

        if (this.childs != null) {
            for (let key in this.childs) {
                if (this.childs[key] instanceof Paylines) {
                    this._paylines = this.childs[key];
                }

                if (this.childs[key] instanceof PaylinesSelector) {
                    this._paylinesSelector = this.childs[key];
                }

                if (this.childs[key] instanceof Symbols) {
                    this._symbols = this.childs[key];
                    this.removeChild(this._symbols);
                }

                if (this.childs[key] instanceof Machine) {
                    this._machine = this.childs[key];
                }

                if (this.childs[key] instanceof WinAnimator) {
                    this._winAnimator = this.childs[key];
                }

                if (this.childs[key] instanceof MinigameElement) {
                    this._minigame = this.childs[key];
                }

                if (key === "TeasingComponent" && this.childs[key] instanceof TeasingComponent) {
                    this._teasingBonus = this.childs[key];
                }

                if (key === "TeasingBack" && this.childs[key] instanceof SwitchElement) {
                    if (this._teasingBonus != null) {
                        this._teasingBonus.addElementInstance({ teasingBack: this.childs[key] });
                    }
                }

                if (key === "TeasingFront" && this.childs[key] instanceof SwitchElement) {
                    if (this._teasingBonus != null) {
                        this._teasingBonus.addElementInstance({ teasingFront: this.childs[key] });
                    }
                }

                if (key === "TeasingParticles" && this.childs[key] instanceof SwitchElement) {
                    if (this._teasingBonus != null) {
                        this._teasingBonus.addElementInstance({ teasingParticles: this.childs[key] });
                    }
                }

                if (key === "TeasingWilds" && this.childs[key] instanceof SwitchElement) {
                    if (this._teasingBonus != null) {
                        this._teasingBonus.addElementInstance({ teasingWilds: this.childs[key] });
                    }
                }

                if (key === "Transition" && this.childs[key] instanceof SwitchElement) {
                    this._transition = this.childs[key];
                }

                if (key === "WildContainer" && this.childs[key] instanceof ContainerElement) {
                    this._wildContainer = this.childs[key];
                }

                if (key === "Background" && this.childs[key] instanceof SwitchElement) {
                    this._background = this.childs[key];
                }

                if (key === "Foreground" && this.childs[key] instanceof SwitchElement) {
                    this._foreground = this.childs[key];
                }
            }

            if (DEBUG) {
                if (this._paylines == null) {
                    this.log("WARNING no paylines");
                }

                if (this._symbols == null) {
                    this.log("ERROR no symbols");
                }

                if (this._machine == null) {
                    this.log("ERROR no machine");
                }

                if (this._winAnimator == null) {
                    this.log("WARNING no winAnimator");
                }
            }
        }
    }

    updateByNode(node: Element): void {
        super.updateByNode(node);

        if (node.hasAttribute(BaseViewAttributes.TRANSITION_ALPHA_TWEEN)) {
            this.transitionAlphaTween = node
                .getAttribute(BaseViewAttributes.TRANSITION_ALPHA_TWEEN)
                .split(";")
                .map((value) => parseFloat(value));
        }
        if (node.hasAttribute(BaseViewAttributes.TRANSITION_ALPHA_TWEEN_KEY_TIMES)) {
            this.transitionAlphaTweenKeyTimes = node
                .getAttribute(BaseViewAttributes.TRANSITION_ALPHA_TWEEN_KEY_TIMES)
                .split(";")
                .map((value) => parseFloat(value));
        }
        if (node.hasAttribute(BaseViewAttributes.TRANSITION_ALPHA_TWEEN_EASE)) {
            this.transitionAlphaTweenEase = node.getAttribute(BaseViewAttributes.TRANSITION_ALPHA_TWEEN_EASE);
        }
        if (node.hasAttribute(BaseViewAttributes.TRANSITION_ALPHA_TWEEN_DELAY)) {
            this.transitionAlphaTweenDelay = parseInt(
                node.getAttribute(BaseViewAttributes.TRANSITION_ALPHA_TWEEN_DELAY),
            );
        }
        if (node.hasAttribute(BaseViewAttributes.TRANSITION_ALPHA_TWEEN_DURATION)) {
            this.transitionAlphaTweenDuration = parseInt(
                node.getAttribute(BaseViewAttributes.TRANSITION_ALPHA_TWEEN_DURATION),
            );
        }
    }

    disableLogo(): void {
        if (this.childs["Logo"] != null) {
            this.removeChild(this.childs["Logo"]);
        }
    }

    initReceived(controller: BaseController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        this._controller = controller;

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.baseResponse != null) {
                    if (GameManager.baseResponse.initData != null && GameManager.baseResponse.initData.reels != null) {
                        this._machine.on(Machine.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(Machine.MachineStopped, this.machineStopped.bind(this));
                        this._machine.initialize(this._symbols, GameManager.baseResponse.initData.reels);
                        this._machine.addElementInstance({ teasing: [this._teasingBonus] });
                    }

                    if (
                        GameManager.baseResponse.screen != null &&
                        GameManager.baseResponse.screen.reels != null &&
                        GameManager.baseResponse.screen.reels.length !== 0
                    ) {
                        if (this._winAnimator != null) {
                            this._winAnimator.on(
                                WinAnimator.FinishWinCalculation,
                                this.finishWinCalculation.bind(this),
                            );
                            this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                            this._winAnimator.on(
                                WinAnimator.FinishQuickWinCalculation,
                                this.finishQuickWinCalculation.bind(this),
                            );
                            this._winAnimator.initialize(
                                this._symbols,
                                this._paylines,
                                this.childs["NoWinContainer"],
                                this.childs["WinContainer"],
                                this,
                            );
                            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
                            this._winAnimator.addElementInstance({ teasing: [this._teasingBonus] });
                            this._winAnimator.addElementInstance({ wildContainer: this._wildContainer });
                        }

                        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reels in response");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no base reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }

        if (ModuleManager.gambleActive()) {
            if (this._foreground != null) {
                this._foreground.goToCase("GAMBLE");
            }

            let childrenArray: any = [];

            for (let key in this.childs) {
                childrenArray.push(this.childs[key]);
            }

            for (let i = 0; i < childrenArray.length; i++) {
                this._gamble.style.top = "0px";

                this._controllerTween = childrenArray[i];
                this._controllerTween.startPos = childrenArray[i].position.y;
                childrenArray[i].position.y = childrenArray[i].position.y - this._gamble.offsetHeight;
            }
        }

        // this.toggleElements(false)
    }

    initIntro(): void {
        if (GameManager.baseResponse.currentWin > 0 || ModuleManager.freespinsEnabled()) {
            PlayList.stop("SND_BaseLoop");
            PlayList.loop("SND_CycleLoop");
        } else {
            PlayList.stop("SND_CycleLoop");
            PlayList.loop("SND_BaseLoop");
        }

        if (ModuleManager.gambleActive()) {
            if (this._foreground != null) {
                this._foreground.goToCase("GAMBLE");
            }

            for (let key in this.childs) {
                this.childs[key].position.y = this.childs[key].position.y - this._gamble.offsetHeight;
            }
        }

        // let min = Math.ceil(1);
        // let max = Math.floor(3);
        // let rnd = "_" + (Math.floor(Math.random() * (max - min)) + min);

        if (this._teasingBonus != null) {
            //this._teasingBonus.getElementInstance()['teasingWilds'].goToCase("INTRO_GAME", false, 30);
        }

        this._winAnimator.showInitWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin);

        // this.toggleElements(true);
    }

    collect(): void {
        PlayList.stop("SND_FSEndLoop");
        PlayList.stop("SND_CycleLoop");
        PlayList.loop("SND_BaseLoop");

        if (this._paylinesSelector != null) {
            this._paylinesSelector.reset();
        }

        this._winAnimator.reset(true);
    }

    spin(): void {
        PlayList.stop("SND_FSEndLoop");
        PlayList.stop("SND_CycleLoop");
        PlayList.loop("SND_BaseLoop");

        if (this._paylinesSelector != null) {
            this._paylinesSelector.reset();
        }

        this._machine.respin = null;
        this._machine.spinStart(this._winAnimator.reset(false, true));
    }

    stop(): void {
        this._machine.spinStop(null, true);
    }

    startReceived(): boolean {
        return true;
    }

    enable(): void {
        this.visible = true;
        this._winAnimator.show();
        this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
        this._controller.startConfigurator();
        this.runTransitoinTweens();
    }

    disable(): void {
        this._winAnimator.hide();
        this._winAnimator.reset(false);
        this.runTransitoinTweens(true);
    }

    public runTransitoinTweens(reverse: boolean = false): boolean {
        if (!this.transitionAlphaTween) {
            this.alpha = reverse ? 0 : 1;
            return false;
        }

        Tweens.removeTweens(this, "alpha");

        const alphas: Array<number> = reverse
            ? this.transitionAlphaTween.slice().reverse()
            : this.transitionAlphaTween.slice();
        const tweenEase: string = this.transitionAlphaTweenEase || "linear";
        const tweenDuration: number = this.transitionAlphaTweenDuration || 120;
        const tweenDelay: number = this.transitionAlphaTweenDelay || 0;
        const tweenLoop: boolean = false;
        const keyTimes: Array<number> = this.transitionAlphaTweenKeyTimes
            ? this.transitionAlphaTweenKeyTimes.slice()
            : null;

        Tweens.alphaTween(this, { alphas, tweenEase, tweenDuration, tweenDelay, tweenLoop, keyTimes });
    }

    spinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    onReelStopped(): void {
        this._winAnimator.onReelStopped(
            GameManager.baseResponse.screen.reels,
            this._machine.actualReelStopped,
            this._machine.forceStop,
        );
    }

    machineStopped(): void {
        this._wasFinished = false;

        let isHighWin = this._winAnimator.isHighWin(GameManager.baseResponse.currentWin);

        this._controller.machineStopped(isHighWin);

        if (this._winAnimator.showWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin)) {
            PlayList.stop("SND_BaseLoop");
        }
    }

    private finishQuickWinCalculation(): void {
        this._controller.quickWinCalculationFinished();
    }

    finishWinCalculation(): void {
        this._wasFinished = true;

        this._controller.winCalculationFinished();
    }

    finishWinAnimator(): void {
        if (!DataManager.Autoplay) {
            PlayList.stop("SND_BaseLoop");
            PlayList.loop("SND_CycleLoop");
        }

        this._controller.allWinsFinished();
    }

    resetWinAnimator(): void {
        this._winAnimator.reset(true);
    }

    intro(long: boolean = false): void {
        //this.toggleElements(true, true);
        // PlayList.stop('SND_CycleLoop');
        // PlayList.loop('SND_BaseLoop');
        if (long) {
            if (this._transition != null) {
                this._transition.goToCase("OUTRO_2");
            }
        }

        let childrenArray: any = [];

        if (this._foreground != null) {
            this._foreground.goToCase("NORMAL");
        }

        for (let key in this.childs) {
            childrenArray.push(this.childs[key]);
        }

        if ((window["CHANNEL"] && (<string>window["CHANNEL"]).toLowerCase()) !== "mobile") {
            childrenArray.push(this._gamble);
        }

        let tween = TweenLite.to(
            childrenArray,
            (window["CHANNEL"] && (<string>window["CHANNEL"]).toLowerCase()) === "mobile"
                ? 0
                : 60 * GsapEffects.TickTime,
            {
                y: "+=" + this._gamble.offsetHeight,
                delay: 1 * GsapEffects.TickTime,
            },
        );
        tween.eventCallback("onComplete", () => {
            if ((window["CHANNEL"] && (<string>window["CHANNEL"]).toLowerCase()) === "mobile") {
                this._gamble.style.top = null;
            }

            this.introFinished();
        });
    }

    introFinished(): void {
        if (GameManager.gameResponse.totalWin > 0) {
            PlayList.stop("SND_BaseLoop");
            if (!PlayList.isPlaying("SND_BaseRecaploop")) {
                PlayList.loop("SND_CycleLoop");
            }
        } else {
            PlayList.stop("SND_CycleLoop");
            PlayList.loop("SND_BaseLoop");
        }

        this._controller.introFinished();
    }

    outro(): void {
        PlayList.stop("SND_BaseLoop");
        PlayList.stop("SND_CycleLoop");

        if (this._paylinesSelector != null) {
            this._paylinesSelector.reset();
        }

        let wasFinished: boolean = this._wasFinished;
        let inHighWins = this._winAnimator.reset(true);

        let childrenArray: any = [];

        if (this._foreground != null) {
            this._foreground.goToCase("GAMBLE");
        }

        for (let key in this.childs) {
            childrenArray.push(this.childs[key]);
        }

        if ((window["CHANNEL"] && (<string>window["CHANNEL"]).toLowerCase()) !== "mobile") {
            childrenArray.push(this._gamble);
        }

        let tween = TweenLite.to(
            childrenArray,
            (window["CHANNEL"] && (<string>window["CHANNEL"]).toLowerCase()) === "mobile"
                ? 0
                : 60 * GsapEffects.TickTime,
            {
                y: "-=" + this._gamble.offsetHeight,
                delay: 80 * GsapEffects.TickTime,
            },
        );
        tween.eventCallback("onComplete", () => {
            if ((window["CHANNEL"] && (<string>window["CHANNEL"]).toLowerCase()) === "mobile") {
                this._gamble.style.top = "0px";
            }
        });

        if (wasFinished) {
            this.outroFinished();
        } else {
            if (inHighWins) {
                this._outroDelay.start(60);
            } else {
                this._outroDelay.start(30);
            }
        }
    }

    gambleMobileDelay(): void {
        this._gamble.style.bottom = "0px";
    }

    outroFinished(): void {
        //this.toggleElements(false, true);
        this._controller.outroFinished();
    }

    startConfigurator(): void {}

    endReceived(): void {}

    selectPayline(): void {
        if (this._paylinesSelector != null) {
            this._paylinesSelector.select();
        }
    }

    toggleElements(visible: boolean, long: boolean = false): void {
        if (long) {
            Tweens.removeTweens(this, "all");
            Tweens.alphaTween(this, {
                alphas: visible ? [0, 1] : [1, 0],
                tweenDuration: 15,
            });
        } else {
            this.alpha = visible ? 1 : 0;
        }
    }

    disableBase(): void {
        this._winAnimator.resetTimer();
    }

    /**
     * Toggle all sounds used in base game (background, effects, .etc)
     * @param enable
     */
    toggleSounds(enable: boolean): void {
        if (enable) {
            PlayList.loop("SND_BaseLoop");
        } else {
            PlayList.stop("SND_BaseLoop");
            PlayList.stop("SND_CycleLoop");
            PlayList.stop("SND_BaseRecaploop");
        }
    }
}

const BaseViewAttributes = {
    TRANSITION_ALPHA_TWEEN: "TransitionAlphaTween",
    TRANSITION_ALPHA_TWEEN_EASE: "TransitionAlphaTweenEase",
    TRANSITION_ALPHA_TWEEN_DELAY: "TransitionAlphaTweenDelay",
    TRANSITION_ALPHA_TWEEN_DURATION: "TransitionAlphaTweenDuration",
    TRANSITION_ALPHA_TWEEN_KEY_TIMES: "TransitionAlphaTweenKeyTimes",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Base/BaseView.ts
 **/